import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  BoltIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  InboxStackIcon,
  ServerStackIcon,
  TableCellsIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import Layout from "../../components/layout/Layout";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectOption,
  Spinner,
  TabularDataViewer,
  type TabularColumn,
} from "../../components/ui";
import { cn } from "../../shared/lib/cn";
import { useToasts } from "../../stores/useToasts";
import {
  useAPInvoiceAvailableConnectors,
  useAPInvoiceDataSourceConfig,
  useAPInvoiceHistory,
  useClearAPInvoiceDataSource,
  useIngestSourceFolderAPInvoices,
  useSetAPInvoiceDataSource,
  useVerifyAPInvoiceConnection,
  useUploadAPInvoice,
} from "./api";
import type {
  APInvoiceAgentTab,
  APInvoiceDataSource,
  APInvoiceHistoryRecord,
} from "./types";
import {
  buildInvoiceHistoryKpis,
  formatCurrency,
  formatPercent,
  routeToTab,
  tabToPath,
} from "./utils";

const PdfPageViewer = React.lazy(
  () => import("../../components/shared/PdfPageViewer"),
);

const TAB_LABELS: Array<{ id: APInvoiceAgentTab; label: string }> = [
  { id: "history", label: "Invoice History" },
  { id: "configuration", label: "Configuration" },
];

const linearPanelClass =
  "!rounded-[12px] !border-gray-200/80 !bg-white !shadow-none dark:!border-dark-border/70 dark:!bg-dark-surface";

const linearButtonBaseClass = "!h-8 !rounded-[6px] !px-3 text-xs";

const linearPrimaryButtonClass = cn(
  linearButtonBaseClass,
  "!border !border-gray-900 !bg-gray-900 !text-white hover:!bg-gray-800 focus-visible:!ring-gray-500 dark:!border-gray-100 dark:!bg-gray-100 dark:!text-gray-950 dark:hover:!bg-white",
);

const linearSecondaryButtonClass = cn(
  linearButtonBaseClass,
  "!border !border-gray-200 !bg-white !text-gray-700 hover:!bg-gray-50 focus-visible:!ring-gray-400 dark:!border-dark-border/70 dark:!bg-dark-surface dark:!text-gray-200 dark:hover:!bg-dark-surface-2",
);

const linearInputClass =
  "!rounded-[8px] !border-gray-200 !text-gray-900 focus:!border-gray-300 focus:!ring-gray-200 dark:!border-dark-border/70 dark:!text-gray-100";

const linearLabelClass = "!text-gray-700 dark:!text-gray-300";

const workbenchPanelClass =
  "overflow-hidden rounded-[12px] border border-gray-200/80 bg-white shadow-[0_1px_1px_rgba(15,23,42,0.02)] dark:border-dark-border/70 dark:bg-dark-surface";

const panelHeaderClass =
  "flex min-h-12 items-center justify-between gap-3 border-b border-gray-200/80 px-4 py-3 dark:border-dark-border/70";

export default function APInvoiceAgentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const pushToast = useToasts((state) => state.push);
  const activeTab = routeToTab(location.pathname);
  const { data, isLoading, isError, refetch } = useAPInvoiceHistory(100);
  const runIntake = useIngestSourceFolderAPInvoices();
  const uploadInvoice = useUploadAPInvoice();
  const config = data?.data_source;
  const kpis = buildInvoiceHistoryKpis(data?.records ?? []);
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(
    null,
  );
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null);
  const [uploadedRecord, setUploadedRecord] =
    useState<APInvoiceHistoryRecord | null>(null);

  useEffect(() => {
    if (!selectedUploadFile) {
      setUploadPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(selectedUploadFile);
    setUploadPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedUploadFile]);

  const selectTab = (tab: APInvoiceAgentTab) => navigate(tabToPath(tab));

  const runSourceFolderIntake = () => {
    runIntake.mutate(
      {
        max_files: 5,
        sample_directory: config?.sample_directory,
        source_mailbox: config?.source_mailbox,
        target_system: config?.target_system,
      },
      {
        onSuccess: (result) =>
          pushToast({
            kind: result.warnings.length ? "warning" : "success",
            message: `Staged ${result.inserted_count} invoice${result.inserted_count === 1 ? "" : "s"}.`,
          }),
        onError: (error) =>
          pushToast({ kind: "error", message: error.message }),
      },
    );
  };

  const selectUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedUploadFile(file);
    setUploadedRecord(null);
  };

  const uploadSelectedInvoice = () => {
    if (!selectedUploadFile) return;
    uploadInvoice.mutate(
      {
        file: selectedUploadFile,
        sourceMailbox: config?.source_mailbox,
        targetSystem: config?.target_system,
      },
      {
        onSuccess: (result) => {
          setUploadedRecord(result.records[0] ?? null);
          pushToast({
            kind: result.warnings.length ? "warning" : "success",
            message: `Staged ${result.inserted_count} invoice${result.inserted_count === 1 ? "" : "s"}.`,
          });
        },
        onError: (error) =>
          pushToast({ kind: "error", message: error.message }),
      },
    );
  };

  return (
    <Layout>
      <div className="flex h-full flex-col bg-gray-50 text-gray-900 dark:bg-dark-bg dark:text-gray-100">
        <div className="border-b border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
          <div className="mx-auto max-w-[1680px] px-5 py-3 lg:px-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Finance operations
                  </span>
                  <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-dark-border" />
                  <Badge
                    variant={config?.is_connected ? "success" : "warning"}
                    className="rounded-[6px] border px-1.5 py-0 text-[11px] font-medium"
                  >
                    {config?.mode ?? "unconfigured"}
                  </Badge>
                  <Badge
                    variant="secondary"
                    className="rounded-[6px] px-1.5 py-0 text-[11px] font-medium"
                  >
                    {config?.target_system ?? "redshift"}
                  </Badge>
                  {kpis.needsReviewCount > 0 && (
                    <Badge
                      variant="warning"
                      className="rounded-[6px] px-1.5 py-0 text-[11px] font-medium"
                    >
                      {kpis.needsReviewCount} review
                    </Badge>
                  )}
                </div>
                <h1 className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                  AP Invoice Agent
                </h1>
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  Invoice intake and bill staging
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className={linearSecondaryButtonClass}
                  onClick={() => refetch()}
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Refresh
                </Button>
                <Button
                  size="sm"
                  className={linearPrimaryButtonClass}
                  onClick={runSourceFolderIntake}
                  disabled={!config?.is_connected || runIntake.isPending}
                >
                  <BoltIcon className="h-4 w-4" />
                  Run Intake
                </Button>
              </div>
            </div>

            <div className="mt-3 inline-flex rounded-[8px] border border-gray-200 bg-gray-50 p-0.5 dark:border-dark-border/70 dark:bg-dark-surface-2">
              {TAB_LABELS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => selectTab(tab.id)}
                  aria-current={activeTab === tab.id ? "page" : undefined}
                  className={cn(
                    "rounded-[6px] px-3 py-1.5 text-xs font-medium transition-[background-color,border-color,color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-dark-surface",
                    activeTab === tab.id
                      ? "bg-white text-gray-900 shadow-sm dark:bg-dark-surface dark:text-gray-100"
                      : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100",
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1680px] px-5 py-4 lg:px-6">
            {isLoading && activeTab === "history" && (
              <div className="flex h-64 items-center justify-center">
                <Spinner size="lg" />
              </div>
            )}

            {isError && activeTab === "history" && (
              <Card>
                <CardContent className="flex items-center gap-3 p-6 text-amber-700">
                  <XCircleIcon className="h-5 w-5" />
                  Configure a data source before loading invoice history.
                </CardContent>
              </Card>
            )}

            {activeTab === "history" && data && (
              <HistoryTab
                rows={data.records}
                kpis={kpis}
                dataSource={data.data_source}
                uploadPanel={
                  <UploadReviewPanel
                    dataSource={data.data_source}
                    kpis={kpis}
                    selectedFile={selectedUploadFile}
                    previewUrl={uploadPreviewUrl}
                    uploadedRecord={uploadedRecord}
                    onFileChange={selectUploadFile}
                    onUpload={uploadSelectedInvoice}
                    isUploading={uploadInvoice.isPending}
                  />
                }
              />
            )}
            {activeTab === "configuration" && <ConfigurationTab />}
          </div>
        </main>
      </div>
    </Layout>
  );
}

function HistoryTab({
  rows,
  kpis,
  dataSource,
  uploadPanel,
}: {
  rows: APInvoiceHistoryRecord[];
  kpis: ReturnType<typeof buildInvoiceHistoryKpis>;
  dataSource: APInvoiceDataSource;
  uploadPanel?: React.ReactNode;
}) {
  const columns = useMemo<TabularColumn<APInvoiceHistoryRecord>[]>(() => {
    const baseColumns: TabularColumn<APInvoiceHistoryRecord>[] = [
      { id: "vendor_name", header: "Vendor", accessorKey: "vendor_name" },
      {
        id: "invoice_number",
        header: "Invoice",
        accessorKey: "invoice_number",
      },
      {
        id: "invoice_date",
        header: "Invoice Date",
        accessorKey: "invoice_date",
      },
      {
        id: "amount",
        header: "Amount",
        accessorKey: "amount",
        type: "currency",
      },
      { id: "po_number", header: "PO", accessorKey: "po_number" },
      { id: "account_code", header: "Account", accessorKey: "account_code" },
      { id: "department", header: "Department", accessorKey: "department" },
      { id: "coding_status", header: "Coding", accessorKey: "coding_status" },
      {
        id: "export_status",
        header: "ERP Status",
        accessorKey: "export_status",
      },
      {
        id: "extraction_confidence",
        header: "Confidence",
        accessorKey: "extraction_confidence",
        cell: ({ value }) => formatPercent(value),
        csvValue: (row) =>
          row.extraction_confidence == null
            ? ""
            : Math.round(row.extraction_confidence * 100),
      },
    ];

    return baseColumns.map((column) => ({
      ...column,
      cellClassName: cn(
        "text-gray-900 dark:text-gray-100",
        column.cellClassName,
      ),
    }));
  }, []);

  return (
    <div className="space-y-4">
      {uploadPanel}

      <div className={workbenchPanelClass}>
        <div className={panelHeaderClass}>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
              Invoice History
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {rows.length.toLocaleString()} staged record
              {rows.length === 1 ? "" : "s"} in invoice history
            </p>
          </div>
          <div className="hidden items-center gap-2 text-xs text-gray-500 dark:text-gray-400 sm:flex">
            <span>{formatCurrency(kpis.totalAmount)}</span>
            <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-dark-border" />
            <span>{formatPercent(kpis.averageConfidence)} avg confidence</span>
          </div>
        </div>
        <div className="p-4">
          <TabularDataViewer
            data={rows}
            columns={columns}
            getRowId={(row) => row.invoice_record_id}
            exportFilename="ap-invoice-history.csv"
            maxHeight="560px"
            compact
            className="[&>div:first-child]:mb-3"
          />
        </div>
      </div>
    </div>
  );
}

function UploadReviewPanel({
  dataSource,
  kpis,
  selectedFile,
  previewUrl,
  uploadedRecord,
  onFileChange,
  onUpload,
  isUploading,
}: {
  dataSource: APInvoiceDataSource;
  kpis: ReturnType<typeof buildInvoiceHistoryKpis>;
  selectedFile: File | null;
  previewUrl: string | null;
  uploadedRecord: APInvoiceHistoryRecord | null;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => void;
  isUploading: boolean;
}) {
  const extractedFields: Array<[string, string | null | undefined]> =
    uploadedRecord
      ? [
          ["Vendor", uploadedRecord.vendor_name],
          ["Invoice", uploadedRecord.invoice_number],
          ["Invoice Date", uploadedRecord.invoice_date],
          [
            "Amount",
            uploadedRecord.amount == null
              ? null
              : formatCurrency(uploadedRecord.amount),
          ],
          [
            "Net",
            uploadedRecord.net_amount == null
              ? null
              : formatCurrency(uploadedRecord.net_amount),
          ],
          [
            "Tax",
            uploadedRecord.tax_amount == null
              ? null
              : formatCurrency(uploadedRecord.tax_amount),
          ],
          ["PO", uploadedRecord.po_number],
          ["Account", uploadedRecord.account_code],
          ["Department", uploadedRecord.department],
          ["Cost Center", uploadedRecord.cost_center],
          ["Coding", uploadedRecord.coding_status],
          ["ERP Status", uploadedRecord.export_status],
          ["Confidence", formatPercent(uploadedRecord.extraction_confidence)],
        ]
      : [];
  const payloadEntries = Object.entries(
    uploadedRecord?.extracted_payload ?? {},
  );
  const canUpload = Boolean(
    selectedFile && dataSource.is_connected && !isUploading,
  );
  const reviewState = uploadedRecord
    ? "Extracted"
    : selectedFile
      ? "Ready"
      : "Waiting";
  const historyTable = `${dataSource.redshift_schema ?? "ap_invoice_agent"}.${dataSource.table_name}`;

  return (
    <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)_420px]">
      <aside className={workbenchPanelClass}>
        <div className={panelHeaderClass}>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-950 dark:text-gray-100">
              Intake
            </h2>
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
              {dataSource.source_mailbox ?? "Source mailbox not set"}
            </p>
          </div>
          <Badge
            variant={dataSource.is_connected ? "success" : "warning"}
            className="rounded-[6px] px-2 py-1 text-[11px]"
          >
            {dataSource.is_connected ? "Connected" : "Setup"}
          </Badge>
        </div>

        <div className="space-y-4 p-4">
          <div>
            <Input
              id="ap-invoice-upload"
              type="file"
              accept="application/pdf"
              onChange={onFileChange}
              className="sr-only"
            />
            <Label
              htmlFor="ap-invoice-upload"
              className="group block cursor-pointer rounded-[10px] border border-dashed border-gray-300 bg-gray-50/70 p-4 !leading-normal transition-[background-color,border-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:border-gray-400 hover:bg-white active:scale-[0.99] dark:border-dark-border/70 dark:bg-dark-surface-2/60 dark:hover:bg-dark-surface-2"
            >
              <span className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-gray-200 bg-white text-gray-500 transition-colors group-hover:text-gray-900 dark:border-dark-border/70 dark:bg-dark-surface dark:text-gray-400 dark:group-hover:text-gray-100">
                  <ArrowUpTrayIcon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-950 dark:text-gray-100">
                    {selectedFile?.name ?? "Select invoice PDF"}
                  </span>
                  <span className="mt-1 block text-xs font-normal text-gray-500 dark:text-gray-400">
                    Secure PDF intake into bill staging.
                  </span>
                </span>
              </span>
            </Label>
          </div>

          <Button
            type="button"
            size="sm"
            className={cn(linearPrimaryButtonClass, "w-full")}
            onClick={onUpload}
            disabled={!canUpload}
          >
            {isUploading ? (
              <Spinner size="sm" />
            ) : (
              <ArrowUpTrayIcon className="h-4 w-4" />
            )}
            Extract and Stage
          </Button>

          <div className="grid grid-cols-2 gap-2">
            <KpiStat
              label="Invoices"
              value={`${kpis.invoiceCount}`}
              icon={<InboxStackIcon className="h-4 w-4" />}
            />
            <KpiStat
              label="Ready"
              value={`${kpis.readyForErpCount}`}
              icon={<CheckCircleIcon className="h-4 w-4" />}
            />
            <KpiStat
              label="Total"
              value={formatCurrency(kpis.totalAmount)}
              icon={<TableCellsIcon className="h-4 w-4" />}
            />
            <KpiStat
              label="Confidence"
              value={formatPercent(kpis.averageConfidence)}
              icon={<BoltIcon className="h-4 w-4" />}
            />
          </div>

          <dl className="space-y-2 border-t border-gray-200/80 pt-4 text-xs dark:border-dark-border/70">
            <Row label="Bill destination" value={dataSource.target_system} />
            <Row
              label="Data connection"
              value={dataSource.connector_name ?? "Not selected"}
            />
            <Row label="History table" value={historyTable} />
          </dl>
        </div>
      </aside>

      <section className={workbenchPanelClass}>
        <div className={panelHeaderClass}>
          <div className="flex min-w-0 items-center gap-2">
            <DocumentTextIcon className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
                Document
              </h2>
              <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                {selectedFile?.name ?? "No invoice selected"}
              </p>
            </div>
          </div>
          <Badge
            variant="secondary"
            className="rounded-[6px] px-2 py-1 text-[11px]"
          >
            {reviewState}
          </Badge>
        </div>

        <div className="min-h-[540px] bg-[#f5f6f8] dark:bg-dark-surface-2">
          {previewUrl ? (
            <React.Suspense
              fallback={
                <div className="flex h-[680px] items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              <PdfPageViewer
                pdfUrl={previewUrl}
                pageNumber={1}
                pageWindow={0}
                className="h-[680px] w-full !bg-[#f5f6f8]"
              />
            </React.Suspense>
          ) : (
            <div className="flex h-[540px] items-center justify-center p-8">
              <div className="w-full max-w-sm rounded-[14px] border border-gray-200 bg-white p-8 text-center shadow-[0_18px_50px_rgba(15,23,42,0.06)] dark:border-dark-border/70 dark:bg-dark-surface">
                <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-4 text-sm font-medium text-gray-950 dark:text-gray-100">
                  Select an invoice to review
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                  The PDF appears here while the right pane tracks extracted
                  fields and confidence.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <aside className={workbenchPanelClass}>
        <div className={panelHeaderClass}>
          <div className="flex min-w-0 items-center gap-2">
            <TableCellsIcon className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
            <h2 className="truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
              Extraction
            </h2>
          </div>
          {uploadedRecord && (
            <Badge
              variant="secondary"
              className="rounded-[6px] px-2 py-1 text-[11px]"
            >
              {formatPercent(uploadedRecord.extraction_confidence)}
            </Badge>
          )}
        </div>

        {uploadedRecord ? (
          <div className="space-y-5 p-4">
            <div className="rounded-[12px] border border-gray-200 bg-gray-50 p-4 dark:border-dark-border/70 dark:bg-dark-surface-2">
              <p className="truncate text-sm font-medium text-gray-950 dark:text-gray-100">
                {uploadedRecord.vendor_name ?? "Unknown vendor"}
              </p>
              <div className="mt-3 flex items-end justify-between gap-3">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Amount
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-950 dark:text-gray-100">
                    {uploadedRecord.amount == null
                      ? "-"
                      : formatCurrency(uploadedRecord.amount)}
                  </p>
                </div>
                <Badge
                  variant={
                    uploadedRecord.export_status === "ready_for_erp"
                      ? "success"
                      : "warning"
                  }
                  className="rounded-[6px] px-2 py-1 text-[11px]"
                >
                  {uploadedRecord.export_status.replace(/_/g, " ")}
                </Badge>
              </div>
            </div>

            <dl className="divide-y divide-gray-200/80 rounded-[10px] border border-gray-200 bg-white text-sm dark:divide-dark-border/70 dark:border-dark-border/70 dark:bg-dark-surface">
              {extractedFields.map(([label, value]) => (
                <ExtractedField
                  key={label}
                  label={label}
                  value={value ?? "-"}
                />
              ))}
            </dl>

            {payloadEntries.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                  Signals
                </h3>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {payloadEntries.map(([key, value]) => (
                    <Badge
                      key={key}
                      variant="secondary"
                      className="rounded-[6px] px-2 py-1 text-[11px]"
                    >
                      {key}: {String(value)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                Raw Text
              </h3>
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-[10px] border border-gray-200 bg-gray-50 p-3 font-mono text-xs leading-5 text-gray-700 dark:border-dark-border/70 dark:bg-dark-surface-2 dark:text-gray-300">
                {uploadedRecord.raw_text_excerpt || "No text captured"}
              </pre>
            </div>
          </div>
        ) : (
          <div className="flex h-[540px] items-center justify-center p-6">
            <div className="text-center">
              <TableCellsIcon className="mx-auto h-7 w-7 text-gray-400" />
              <p className="mt-3 text-sm font-medium text-gray-950 dark:text-gray-100">
                No extraction yet
              </p>
              <p className="mt-1 max-w-56 text-xs leading-5 text-gray-500 dark:text-gray-400">
                Select a PDF to populate vendor, invoice, PO, coding, and
                staging status.
              </p>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function ConfigurationTab() {
  const pushToast = useToasts((state) => state.push);
  const { data: config, isLoading: configLoading } =
    useAPInvoiceDataSourceConfig();
  const { data: connectors, isLoading: connectorsLoading } =
    useAPInvoiceAvailableConnectors();
  const setDataSource = useSetAPInvoiceDataSource();
  const clearDataSource = useClearAPInvoiceDataSource();
  const verifyConnection = useVerifyAPInvoiceConnection();
  const [connectorId, setConnectorId] = useState("");
  const [schema, setSchema] = useState("ap_invoice_agent");
  const [sourceMailbox, setSourceMailbox] = useState("");
  const [targetSystem, setTargetSystem] = useState("redshift");
  const [sourceFolder, setSourceFolder] = useState("");

  useEffect(() => {
    if (!config) return;
    setConnectorId(config.connector_id ?? "");
    setSchema(config.redshift_schema ?? "ap_invoice_agent");
    setSourceMailbox(config.source_mailbox ?? "");
    setTargetSystem(config.target_system ?? "redshift");
    setSourceFolder(config.sample_directory ?? "");
  }, [config]);

  const payload = {
    connector_id: connectorId,
    redshift_schema: schema || "ap_invoice_agent",
    source_mailbox: sourceMailbox || null,
    target_system: targetSystem || "redshift",
    sample_directory: sourceFolder || null,
  };

  const save = () => {
    if (!connectorId) {
      pushToast({ kind: "error", message: "Select a data connection." });
      return;
    }
    setDataSource.mutate(payload, {
      onSuccess: () =>
        pushToast({
          kind: "success",
          message: "AP invoice agent configuration saved.",
        }),
      onError: (error) => pushToast({ kind: "error", message: error.message }),
    });
  };

  const verify = () => {
    if (!connectorId) return;
    verifyConnection.mutate(payload, {
      onSuccess: (result) =>
        pushToast({
          kind: result.redshift_connected ? "success" : "warning",
          message: result.message || "Connection verified.",
        }),
      onError: (error) => pushToast({ kind: "error", message: error.message }),
    });
  };

  return (
    <div className="grid gap-3 xl:grid-cols-[0.9fr_1.1fr]">
      <Card className={linearPanelClass}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <ServerStackIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Current setup
            </h2>
          </div>

          {configLoading ? (
            <div className="mt-6 flex justify-center">
              <Spinner />
            </div>
          ) : (
            <dl className="mt-4 space-y-3 text-sm">
              <Row label="Mode" value={config?.mode ?? "unconfigured"} />
              <Row
                label="Data connection"
                value={config?.connector_name ?? "Not selected"}
              />
              <Row
                label="History table"
                value={`${config?.redshift_schema ?? "ap_invoice_agent"}.${config?.table_name ?? "ap_invoice_history"}`}
              />
              <Row
                label="Source mailbox"
                value={config?.source_mailbox ?? "Not set"}
              />
              <Row label="Bill destination" value={config?.target_system ?? "redshift"} />
            </dl>
          )}
        </CardContent>
      </Card>

      <Card className={linearPanelClass}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <WrenchScrewdriverIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Connection setup
            </h2>
          </div>
          <div className="mt-4 space-y-4">
            <div>
              <Label
                htmlFor="ap-invoice-connector"
                className={linearLabelClass}
              >
                Data connection
              </Label>
              <Select
                value={connectorId}
                onValueChange={setConnectorId}
                disabled={connectorsLoading}
              >
                <SelectOption value="">Select connector</SelectOption>
                {(connectors ?? []).map((connector) => (
                  <SelectOption
                    key={connector.connector_id}
                    value={connector.connector_id}
                  >
                    {connector.connector_name}
                  </SelectOption>
                ))}
              </Select>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="ap-invoice-schema" className={linearLabelClass}>
                  History schema
                </Label>
                <Input
                  id="ap-invoice-schema"
                  value={schema}
                  onChange={(event) => setSchema(event.target.value)}
                  placeholder="ap_invoice_agent"
                  className={linearInputClass}
                />
              </div>
              <div>
                <Label htmlFor="ap-invoice-target" className={linearLabelClass}>
                  Bill destination
                </Label>
                <Input
                  id="ap-invoice-target"
                  value={targetSystem}
                  onChange={(event) => setTargetSystem(event.target.value)}
                  placeholder="netsuite"
                  className={linearInputClass}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ap-invoice-mailbox" className={linearLabelClass}>
                Source mailbox
              </Label>
              <Input
                id="ap-invoice-mailbox"
                value={sourceMailbox}
                onChange={(event) => setSourceMailbox(event.target.value)}
                placeholder="ap@example.com"
                className={linearInputClass}
              />
            </div>
            <div>
              <Label
                htmlFor="ap-invoice-source-folder"
                className={linearLabelClass}
              >
                Batch intake folder
              </Label>
              <Input
                id="ap-invoice-source-folder"
                value={sourceFolder}
                onChange={(event) => setSourceFolder(event.target.value)}
                placeholder="/path/to/ap-invoice-pdfs"
                className={linearInputClass}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className={linearPrimaryButtonClass}
                onClick={save}
                disabled={setDataSource.isPending}
              >
                Save settings
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className={linearSecondaryButtonClass}
                onClick={verify}
                disabled={!connectorId || verifyConnection.isPending}
              >
                Test connection
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={linearSecondaryButtonClass}
                onClick={() =>
                  clearDataSource.mutate(undefined, {
                    onSuccess: () =>
                      pushToast({
                        kind: "info",
                        message: "AP invoice agent configuration cleared.",
                      }),
                  })
                }
                disabled={clearDataSource.isPending}
              >
                Reset settings
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiStat({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-[10px] border border-gray-200/80 bg-white p-3 dark:border-dark-border/70 dark:bg-dark-surface">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <span className="text-gray-400 dark:text-gray-500">{icon}</span>
      </div>
      <p className="mt-2 truncate text-base font-semibold text-gray-950 dark:text-gray-100">
        {value}
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-3">
      <dt className="shrink-0 text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className="min-w-0 truncate font-medium text-gray-900 dark:text-gray-100"
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}

function ExtractedField({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-3 px-3 py-2.5">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className="min-w-0 truncate font-medium text-gray-900 dark:text-gray-100"
        title={value}
      >
        {value}
      </dd>
    </div>
  );
}
